import React, { useEffect, useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { LoadingIndicator } from '@ygtechnologies/bygr-frontend-component-library';
import DocumentList from '@/seller/components/documents/DocumentList/DocumentList';
import { searchResidentialUnitDocuments, ResidentialUnitDocumentSearchOptions } from '@/services/documents';
import ResidentialUnitGrouping from '../residential-units/ResidentialUnitGrouping/ResidentialUnitGrouping';
import { ChangedItem, BaseDocument } from '@/types';
import ResidentialUnitGroupingHeader from '../residential-units/ResidentialUnitGrouping/ResidentialUnitGroupingHeader';
import { useDefaultScrollToStringId } from '@/utils/hooks';
import { Checkbox } from '@ygtechnologies/rubus-ui';
import DocumentListSlideup from './DocumentList/DocumentListSlideup';
import theme from './DocumentList/DocumentList.module.scss';

interface UnitDocumentFilterableListProps {
    filters: ResidentialUnitDocumentSearchOptions;
    projectId: number;
}

const UnitDocumentFilterableList: React.FC<UnitDocumentFilterableListProps> = ({ filters, projectId }) => {
    const [documents, setDocuments] = useState<BaseDocument[] | null>(null);
    const [selectedDocuments, setSelectedDocuments] = useState<BaseDocument[]>([]);
    const defaultExpandedId = useDefaultScrollToStringId();
    useEffect(() => {
        async function loadData() {
            setDocuments(null);
            setSelectedDocuments([]);
            var r = await searchResidentialUnitDocuments({ ...filters, projectId });
            setDocuments(r);
        }

        loadData();
    }, [filters, projectId]);

    const handleChange = async (changes: ChangedItem<BaseDocument>[]) => {
        if (!documents) return;
        setDocuments(await searchResidentialUnitDocuments({ ...filters, projectId, includeUnpublishedVersionsCount: true }));
    };

    if (!documents) return <LoadingIndicator />;

    const allSelected =
        documents.length > 0 &&
        documents.every((doc) => selectedDocuments.some((selected) => selected.documentId === doc.documentId));

    const handleGlobalSelectAll = (checked: boolean) => {
        if (checked) setSelectedDocuments(documents);
        else setSelectedDocuments([]);
    };

    const handleSelectionChange = (newSelectedItems: BaseDocument[]) => {
        setSelectedDocuments(newSelectedItems);
    };

    if (!filters.residentialUnitIds) {
        return (
            <>
                {documents.length > 0 && (
                    <div className={theme.selectAll}>
                        <Checkbox
                            checked={allSelected}
                            onChange={(e) => handleGlobalSelectAll(e.target.checked)}
                            inputSize="md">
                            {allSelected ? <Trans>Deselect all</Trans> : <Trans>Select all</Trans>}
                        </Checkbox>
                    </div>
                )}
                <ResidentialUnitGrouping
                    projectId={projectId}
                    items={documents}
                    emptyText={<DocumentList mode="ResidentialUnit" documents={[]} />}>
                    {(items, residentialUnit, residentialUnitGroup) => (
                        <React.Fragment key={residentialUnit.id}>
                            <ResidentialUnitGroupingHeader
                                residentialUnit={residentialUnit}
                                residentialUnitGroup={residentialUnitGroup}
                                detailsLinkTab="documents"
                            />
                            <DocumentList
                                defaultExpandedId={defaultExpandedId}
                                mode="ResidentialUnit"
                                documents={items}
                                onChange={handleChange}
                                selectedItems={selectedDocuments.filter((doc) =>
                                    items.some((item) => item.documentId === doc.documentId)
                                )}
                                onSelectionChange={(newItems) => {
                                    const otherSelected = selectedDocuments.filter(
                                        (doc) => !items.some((item) => item.documentId === doc.documentId)
                                    );
                                    handleSelectionChange([...otherSelected, ...newItems]);
                                }}
                                hideSelectAll={true}
                            />
                        </React.Fragment>
                    )}
                </ResidentialUnitGrouping>
                <DocumentListSlideup
                    isOpen={selectedDocuments.length > 0}
                    onCloseDialog={() => setSelectedDocuments([])}
                    selectedDocuments={selectedDocuments}
                    onChange={() => handleChange([])}
                />
            </>
        );
    }

    return (
        <DocumentList
            defaultExpandedId={defaultExpandedId}
            mode="ResidentialUnit"
            documents={documents}
            onChange={handleChange}
        />
    );
};

export default UnitDocumentFilterableList;
